import { Button, Flex } from '@chakra-ui/react';
import { useShowToast } from '@/hooks';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import { SurveyTabs } from '@/pages/ats-job-creation/tabs/interview-plan/stage/activity/activity-sections/survey/components';
import { ScheduleForLater } from '../assessment/send-assessment/schedule/ScheduleForLater';
import { useSurveyFormState } from './hooks/useSurveyFormState';
import { useSurveyFormSubmit } from './hooks/useSurveyFormSubmit';
import { getSurveyTabsConfig } from './config/survey-tabs.config';
import { getInvalidVariableError } from '@/utils/commonHelpers';

interface SurveyFormProps {
  activity: any;
  isCreateFlow: boolean;
  isLoading?: boolean;
  onSave: (_params: any) => Promise<{ data?: any; errors?: any[] }>;
  onCancel: () => void;
  showBackButton?: boolean;
  saveButtonText?: string;
  successMessage?: {
    create: string;
    update: string;
  };
  errorMessage?: {
    create: string;
    update: string;
  };

  hasSchedule?: boolean;
  isScheduled?: boolean;
  setIsScheduled?: (_value: boolean) => void;
  isInterviewPlan?: boolean;
}

export const SurveyForm: React.FC<SurveyFormProps> = ({
  activity,
  isCreateFlow,
  isLoading = false,
  onSave,
  onCancel,
  saveButtonText,
  successMessage = {
    create: 'Activity created successfully',
    update: 'Activity updated successfully',
  },
  errorMessage = {
    create: 'Activity creation failed',
    update: 'Activity updation failed',
  },

  hasSchedule = false,
  isScheduled: editedIsScheduled,
  setIsScheduled: editedSetIsScheduled,
  isInterviewPlan = false,
}) => {
  const toast = useShowToast();
  const { processFromUserPayload, feSharedEmails } = useGetAllUsers();

  const {
    currentTab,
    setCurrentTab,
    selectedTemplate,
    surveyType,
    setSurveyType,
    detailsRef,
    formEditorRef,
    scheduleRef,
    selectOnChange,
    updateSurveyTypeFromDetails,
    handleDetailsTabSubmit,
    handleCancelSchedule,
    isScheduled,
    setIsScheduled,
  } = useSurveyFormState({
    activity,
    isScheduled: editedIsScheduled,
    setIsScheduled: editedSetIsScheduled,
  });

  const { submitSurvey } = useSurveyFormSubmit({
    isCreateFlow,
    successMessage,
    errorMessage,
    onSave,
    selectedTemplate,
    feSharedEmails,
    processFromUserPayload,
    isInterviewPlan,
  });

  const surveyTabs = getSurveyTabsConfig(
    detailsRef,
    formEditorRef,
    activity,
    surveyType,
    setSurveyType,
    selectedTemplate,
    selectOnChange,
    isScheduled,
    isInterviewPlan
  );

  const getNextTab = () => {
    const currentIndex = surveyTabs.findIndex((tab) => tab.id === currentTab);
    return surveyTabs?.[currentIndex + 1];
  };

  const handleEmailTabSubmit = async () => {
    await (formEditorRef?.current as any)?.submitForm();
    await (scheduleRef?.current as any)?.submitForm();
    if (!(formEditorRef?.current as any)?.isValid) {
      toast({
        title: 'Please fill all required fields',
        status: 'error',
      });
      return;
    }

    const detailsValues = (detailsRef?.current as any)?.values || {};
    const emailValues = (formEditorRef?.current as any)?.values || {};

    const scheduleValues = isScheduled
      ? (scheduleRef?.current as any)?.values || {}
      : undefined;

    await submitSurvey(detailsValues, emailValues, scheduleValues);
  };

  const handleSchedule = async (scheduleValues: any) => {
    await (detailsRef?.current as any)?.submitForm();
    await (formEditorRef?.current as any)?.submitForm();

    if (
      !(detailsRef?.current as any)?.isValid ||
      !(formEditorRef?.current as any)?.isValid
    ) {
      toast({
        title: 'Please fill all required fields',
        status: 'error',
      });
      return;
    }

    const detailsValues = (detailsRef?.current as any)?.values || {};
    const emailValues = (formEditorRef?.current as any)?.values || {};

    const result = await submitSurvey(
      detailsValues,
      emailValues,
      scheduleValues,
      true
    );

    if (result.success) {
      toast({ title: 'Survey scheduled successfully' });
      setIsScheduled(true);
    } else {
      toast({
        title: 'Failed to schedule survey',
        status: 'error',
        description:
          result?.errors?.length > 0
            ? getInvalidVariableError(result.errors[0])
            : 'Something went wrong please try again later or contact support@kula.ai',
      });
    }
  };

  const handleSubmit = async () => {
    if (currentTab === 'details') {
      await handleDetailsTabSubmit();
    } else if (currentTab === 'email') {
      await handleEmailTabSubmit();
    }
  };

  const getFinalSaveButtonText = () => {
    if (saveButtonText) return saveButtonText;
    if (currentTab !== 'email') return `Next: ${getNextTab()?.label}`;
    return isCreateFlow
      ? isInterviewPlan
        ? 'Add survey'
        : 'Send survey'
      : 'Save survey';
  };

  return (
    <Flex flexDir="column" flex="1" minH="0" overflow="hidden">
      <SurveyTabs
        tabs={surveyTabs}
        isCreateFlow={isCreateFlow}
        currentTab={currentTab}
        onTabChange={setCurrentTab}
        onTabValidation={async (fromTab, toTab) => {
          if (fromTab === 'details' && toTab === 'email') {
            await (detailsRef?.current as any)?.submitForm();
            if ((detailsRef?.current as any)?.isValid) {
              updateSurveyTypeFromDetails();
            }
            return (detailsRef?.current as any)?.isValid;
          }
          return true;
        }}
        activity={activity}
        scheduling={{
          handleSchedule: handleSchedule,
          handleCancelSchedule: handleCancelSchedule,
          isScheduling: isLoading,
          isCanceling: false,
          scheduleRef: scheduleRef,
        }}
      />
      <Flex
        px="24px"
        py="16px"
        borderTop="1px solid"
        borderColor="border-color"
        bg="ats-secondary"
        position="absolute"
        bottom="0"
        width="100%"
        zIndex="10"
      >
        {/* commenting out back button for now */}
        {/* {showBackButton && currentTab === 'email' && (
          <Button variant="secondary" onClick={() => setCurrentTab('details')}>
            Back
          </Button>
        )} */}
        <Flex ml="auto" gap="12px">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          {currentTab === 'email' && hasSchedule && !isScheduled && (
            <ScheduleForLater
              scheduleRef={scheduleRef}
              scheduleDetails={[]}
              onSchedule={(values) => {
                handleSchedule(values);
                setIsScheduled(true);
              }}
              onCancelSchedule={handleCancelSchedule}
              isScheduling={isLoading}
            />
          )}
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isLoading}
          >
            {getFinalSaveButtonText()}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
